import React from 'react';

import { WebUiButton } from '../../../components/button';
import { WebUiInput } from '../../../components/input';
import { WebUiSelect } from '../../../components/select';
import type { InterfaceCfg } from './types';

export default function PortsConfigTable({
  cfg,
  saving,
  updateCfg,
  onSave,
}: {
  cfg: InterfaceCfg[];
  saving: boolean;
  updateCfg: (id: number, patch: Partial<InterfaceCfg>) => void;
  onSave: () => Promise<void>;
}) {
  return (
    <div className="w-full">
      <h4 className="font-bold">Port Configuration</h4>
      <table className="webui-table w-full">
        <thead>
          <tr className="webui-table-row-head">
            <th className="text-center">Port</th>
            <th>Name</th>
            <th>Link</th>
            <th>Auto Negotiation</th>
            <th>Speed</th>
            <th>Duplex</th>
            <th>MDI-X</th>
            <th>ITxPT xstatus</th>
          </tr>
        </thead>
        <tbody>
          {cfg.map((p) => {
            const lockSpeedDuplex = p.autoneg;
            const lockDuplex = p.speed >= 1000;
            return (
              <tr key={p.interface} className="webui-table-row-content">
                <td className="text-center">{p.interface}</td>
                <td className="text-center">
                  <WebUiInput
                    inputSize="sm"
                    type="text"
                    value={p.name}
                    onChange={(e) =>
                      updateCfg(p.interface, { name: e.target.value })
                    }
                  />
                </td>
                <td className="text-center">
                  <WebUiSelect
                    inputSize="sm"
                    className="w-full"
                    options={[
                      { label: 'enabled', value: 'true' },
                      { label: 'disabled', value: 'false' },
                    ]}
                    value={p.up ? 'true' : 'false'}
                    onChange={(e) =>
                      updateCfg(p.interface, { up: e.target.value === 'true' })
                    }
                  />
                </td>
                <td className="text-center">
                  <WebUiSelect
                    inputSize="sm"
                    className="w-full"
                    options={[
                      { label: 'enabled', value: 'true' },
                      { label: 'disabled', value: 'false' },
                    ]}
                    value={p.autoneg ? 'true' : 'false'}
                    onChange={(e) =>
                      updateCfg(p.interface, {
                        autoneg: e.target.value === 'true',
                      })
                    }
                  />
                </td>
                <td className="text-center">
                  <WebUiSelect
                    inputSize="sm"
                    className="w-full"
                    options={[
                      { label: '10', value: '10' },
                      { label: '100', value: '100' },
                    ]}
                    disabled={lockSpeedDuplex}
                    value={Math.min(100, p.speed).toString()}
                    onChange={(e) =>
                      updateCfg(p.interface, {
                        speed: Number(e.target.value) as InterfaceCfg['speed'],
                      })
                    }
                  />
                </td>
                <td className="text-center">
                  <WebUiSelect
                    inputSize="sm"
                    className="w-full"
                    options={[
                      { label: 'full', value: 'true' },
                      { label: 'half', value: 'false' },
                    ]}
                    disabled={lockSpeedDuplex || lockDuplex}
                    value={lockDuplex ? 'true' : 'false'}
                    onChange={(e) =>
                      updateCfg(p.interface, {
                        duplex: e.target.value === 'true',
                      })
                    }
                  />
                </td>
                <td className="text-center">
                  <WebUiSelect
                    inputSize="sm"
                    className="w-full"
                    options={[
                      { label: 'auto', value: 'auto' },
                      { label: 'mdi', value: 'mdi' },
                      { label: 'mdix', value: 'mdix' },
                    ]}
                    value={p.mdi as InterfaceCfg['mdi']}
                    onChange={(e) =>
                      updateCfg(p.interface, {
                        mdi: e.target.value as InterfaceCfg['mdi'],
                      })
                    }
                  />
                </td>
                <td className="text-center">
                  <WebUiInput
                    inputSize="sm"
                    type="checkbox"
                    value={p.diagnostics ? 'true' : 'false'}
                    onChange={(e) =>
                      updateCfg(p.interface, {
                        diagnostics: e.target.checked,
                      })
                    }
                  />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      <div className="mt-2">
        <WebUiButton disabled={saving} onClick={() => onSave().catch(() => {})}>
          Set Port Configuration
        </WebUiButton>
      </div>
    </div>
  );
}
